/**
 * Button primitive — knappene fra den grønne handoffen (#152).
 *
 * Varianter:
 *   - primary    hovedhandlingen: `bg-primary text-on-primary`. Lime i mørkt
 *                tema og dyp grønn i lyst — begge verdiene bor i tokenet.
 *   - solid      ink-farget: «Ta», valgte tilstander (`bg-solid text-on-solid`).
 *   - secondary  omrisset glass (`bg-secondary` + `border-secondary-line`).
 *   - ghost      lav vekt, lenkeaktig (`text-ink-2`).
 *   - danger     destruktiv: `bg-bad` med `text-sheet` (paret står i
 *                contrast.test.ts).
 *
 * Størrelse: `size` er høyden i px, 44 / 52 / 56 (prinsipp 5 i HANDOFF.md:
 * mål ≥ 44 px, hovedknapper 52–56). Uten `size` får knappen bare `min-h-11`,
 * slik at kallsteder som setter egen høyde (`h-12`) beholder den til de
 * migreres i sine steg.
 *
 * Deaktivert tilstand: aria-disabled + pointer-events-none + opacity-40, så
 * knappen fortsatt er synlig og leselig.
 *
 * React 19: `ref` er en vanlig prop, så komponenten bruker ikke forwardRef.
 */
import type { ComponentPropsWithRef, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'solid' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 44 | 52 | 56;

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:opacity-90',
  solid: 'bg-solid text-on-solid hover:opacity-90',
  secondary: 'bg-secondary text-ink border border-secondary-line hover:bg-inset',
  ghost: 'text-ink-2 hover:text-ink',
  danger: 'bg-bad text-sheet hover:opacity-90',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  44: 'h-11 px-4.5 text-sm',
  52: 'h-13 px-5.5 text-sm',
  56: 'h-14 px-6 text-base',
};

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-pill font-extrabold transition-opacity';

interface ButtonOwnProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

type ButtonProps = ComponentPropsWithRef<'button'> & ButtonOwnProps;

export function Button({
  variant = 'primary',
  size,
  className,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const sizeClass = size ? SIZE_CLASS[size] : 'min-h-11 px-4.5 text-sm';
  const disabledClass = disabled ? 'opacity-40 pointer-events-none' : '';
  return (
    <button
      type={type}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      className={[BASE, VARIANT_CLASS[variant], sizeClass, disabledClass, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </button>
  );
}
